"use client";

import { useEffect, useRef, useState } from "react";
import { useAdmin } from "@/lib/admin/context";
import { Icon } from "@/lib/icons/Icon";
import styles from "./AdminLocaleSwitch.module.css";

/**
 * AdminLocaleSwitch — pokazuje którym językiem admin AKTUALNIE zarządza
 * + dropdown z możliwością przełączenia na inną wersję językową.
 *
 * **Model danych:** w Imperial każdy język to osobna "strona" CMS — różny
 * układ (`imp_cms_catalog_layout` per `id_domain_language`), różne przypisania
 * okien (`imp_cms_catalog_window`) i różne dane konfiguracyjne. Przełącznik
 * pozwala adminowi szybko zmienić kontekst zarządzania bez nawigowania na
 * inną podstronę — kliknięcie wykonuje pełne przejście (`<a href>`) na
 * homepage wybranej wersji językowej (URL zbudowany przez backend ILINK).
 *
 * **Trigger:** zawsze pokazuje aktualny język (`current` z `domainLanguages`
 * lub fallback do `locale` z URL'a gdy backend listy nie zwrócił).
 *
 * **Menu:** lista wszystkich aktywnych języków domeny z aktualnym zaznaczonym.
 * Kliknięcie = nawigacja `<a href>` (server-side load nowej locale).
 *
 * **Click-outside:** zamyka dropdown (parity z `WLanguageVersions` dropdown
 * mode + `UserMenuDropdown`).
 *
 * Renderowany jako część `AdminTopBar` -> automatycznie ukryty dla visitor'a
 * (top bar nie renderuje się bez session + scope).
 */
export function AdminLocaleSwitch() {
  const { domainLanguages, t } = useAdmin();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  /* Click-outside zamyka dropdown. Identyczny pattern jak w
     `WLanguageVersions` `DropdownMode` i `UserMenuDropdown`. */
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  /* Pusty stan — backend nie zwrócił żadnych aktywnych języków. Nie pokazujemy
     przycisku w ogóle (admin nie ma czym przełączać). */
  if (domainLanguages.length === 0) {
    return null;
  }

  /* Resolve aktualnego języka — wpis z `current=true` z backend response.
     Gdy brak (np. URL ma locale nieskonfigurowane w domenie) -> label `?`
     ostrzega admina że coś nie pasuje, ale menu nadal pozwala wybrać. */
  const current = domainLanguages.find((l) => l.current);
  const label = current?.name ?? "?";
  const shortCode = current ? current.symbol.toUpperCase() : "?";

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={open ? `${styles.trigger} ${styles.triggerOpen}` : styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        title={t.topBar.adminLocaleSwitch.triggerTitle(label)}
      >
        <Icon name="translate" className={styles.icon} />
        <span className={styles.code}>{shortCode}</span>
        <span className={styles.label}>{label}</span>
        <span className={styles.arrow} aria-hidden="true">
          {open ? "▴" : "▾"}
        </span>
      </button>
      {open && (
        <ul className={styles.menu} role="listbox" aria-label={t.topBar.adminLocaleSwitch.menuAriaLabel}>
          <li className={styles.menuHeader} aria-hidden="true">
            {t.topBar.adminLocaleSwitch.menuHeader}
          </li>
          {domainLanguages.map((lang) => (
            <li key={lang.id} role="option" aria-selected={lang.current}>
              <a
                href={lang.href}
                hrefLang={lang.symbol}
                className={
                  lang.current
                    ? `${styles.menuItem} ${styles.menuItemActive}`
                    : styles.menuItem
                }
                /* full server-side load — admin context (cookies, scopes)
                   zostają, tylko `s_lang` w URL'u się zmieni -> backend
                   resolve nowy `id_domain_language` -> SSR re-render z
                   nowym układem + oknami + cmsConfig per-language. */
              >
                <span className={styles.menuCode}>
                  {lang.symbol.toUpperCase()}
                </span>
                <span className={styles.menuLabel}>{lang.name}</span>
                {lang.current && (
                  <span className={styles.menuCheck} aria-hidden="true">
                    ✓
                  </span>
                )}
              </a>
            </li>
          ))}
          {/* Hint dla admina — żeby nie mylić tego z user-facing WLanguageVersions */}
          <li className={styles.menuFooter} aria-hidden="true">
            {t.topBar.adminLocaleSwitch.menuFooter}
          </li>
        </ul>
      )}
    </div>
  );
}
